import React, { useEffect, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { applyMaskPhoneNumber, validatePhoneNumber } from '../helpers/phoneNumber';
import { states } from '../constants/webphoneStates';
import { AxiosError } from 'axios';

import Loader from '../components/loader/loader';
import styles from '../styles/pages/toCall.module.css';
import Calling from '../components/calling/calling';
import zenviaScriptService from '../services/zenvia/zenvia-script.service';
import Call from '../components/call/call';
import api4comScriptService from 'services/api4com/api4com-script.service';

interface IToCallContext {
  numberCall: string;
  webphoneStates: string;

  handleUpdateNumberCall: (number: string) => void;
  handleStopCall: () => void;
  handleStartCall: () => void;
  handleMuteCall: () => void;
  handleUraCall: (number: string) => void;
}

export const ToCallContext = React.createContext<IToCallContext>({} as IToCallContext);

/**
 * The page ToCall
 * @returns the jsx element
 */
export default function ToCall(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [numberCall, setNumberCall] = useState('');
  const [webphoneStates, setWebphoneStates] = useState<string>(states.CONNECTED);
  const { webphoneToken } = useParams();
  const { provider } = useParams();

  const location = useLocation();
  const closeOnEnd = new URLSearchParams(location.search).get('close-on-end');
  const phoneNumber = new URLSearchParams(location.search).get('phone-number');

  const navigate = useNavigate();

  /**
   * Make a call on zenvia
   */
  function handleStartCall(): void {
    const { phoneNumber } = applyMaskPhoneNumber(numberCall);
    const numberToCall = parseInt(phoneNumber, 10);

    if (provider === 'api4com') {
      void api4comScriptService
        .startCall(numberToCall)
        .then(() => {
          setWebphoneStates(states.CALLING);
        })
        .catch((error) => {
          const { response } = error as AxiosError;
          const options = { state: { status: response?.status } };
          navigate('/error', options);
        });
    } else {
      void zenviaScriptService
        .startCall(webphoneToken || '', numberToCall)
        .then(() => {
          setWebphoneStates(states.CALLING);
        })
        .catch((error) => {
          const { response } = error as AxiosError;
          const options = { state: { status: response?.status } };
          navigate('/error', options);
        });
    }
  }

  /**
   * Update status to TALKING
   */
  function handleTalkingCall(): void {
    setWebphoneStates(states.TALKING);
  }

  /**
   * Connected to handle Zenvia script
   */
  function handleScriptConnected(): void {
    setIsLoading(false);
  }

  /**
   * Error to handle Zenvia script
   */
  function handleScriptError(): void {
    setIsLoading(false);
    navigate('/error');
  }

  /**
   * Update the phone number with mask
   * @param number the phone number
   */
  function handleUpdateNumberCall(number: string): void {
    const newNumber = applyMaskPhoneNumber(number);

    if (newNumber.phoneNumber.length < 12) {
      setNumberCall(newNumber.phoneMaskedNumber);
    }
  }

  /**
   * Stop of the call.
   * Update status to CLOSED.
   * after timeout 2000, update status to CONNECT
   */
  function handleStopCall(isCallback?: boolean): void {
    if (provider === 'api4com') {
      if (isCallback === true) {
        setWebphoneStates(states.CLOSED);

        setTimeout(() => {
          if (closeOnEnd === 'true') {
            window.opener = null;
            window.open('', '_self');
            window.close();
          } else {
            setWebphoneStates(states.CONNECTED);
            setNumberCall('');
          }
        }, 2000);
      } else {
        api4comScriptService.stopCall();
      }
    } else {
      zenviaScriptService.stopCall();

      setWebphoneStates(states.CLOSED);

      setTimeout(() => {
        if (closeOnEnd === 'true') {
          window.opener = null;
          window.open('', '_self');
          window.close();
        } else {
          setWebphoneStates(states.CONNECTED);
          setNumberCall('');
        }
      }, 2000);
    }
  }

  /**
   * Mute of the call.
   * Update status to CLOSED.
   * after timeout 2000, update status to CONNECT
   */
  function handleMuteCall(): void {
    if (provider === 'api4com') {
      api4comScriptService.mute();
    } else {
      zenviaScriptService.mute();
    }
  }

  /**
   * Ura of the call.
   * Update status to CLOSED.
   * after timeout 2000, update status to CONNECT
   */
  function handleUraCall(number: string): void {
    if (provider === 'api4com') {
      api4comScriptService.ura(+number.slice(-1));
    } else {
      api4comScriptService.ura(+number.slice(-1));
    }
  }

  useEffect(() => {
    if (phoneNumber && validatePhoneNumber(phoneNumber)) {
      handleUpdateNumberCall(phoneNumber);
    }
  }, [phoneNumber]);

  useEffect(() => {
    setIsLoading(true);

    if (provider === 'api4com') {
      void api4comScriptService.initApi4comScript(
        webphoneToken || '',
        navigate,
        handleTalkingCall,
        handleScriptConnected,
        handleScriptError,
        handleStopCall,
      );
    } else {
      void zenviaScriptService.initZenviaScript(
        webphoneToken || '',
        navigate,
        handleStopCall,
        handleTalkingCall,
        handleScriptConnected,
        handleScriptError,
      );
    }
  }, []);

  const toCallContext: IToCallContext = {
    numberCall,
    webphoneStates,

    handleUpdateNumberCall,
    handleStopCall,
    handleStartCall,
    handleMuteCall,
    handleUraCall,
  };

  return (
    <>
      {isLoading ? <Loader /> : null}
      <div className={styles.ToCallContainer}>
        <audio style={{ opacity: 0, position: 'absolute', zIndex: -1 }} id="remoteAudio" controls></audio>
        <ToCallContext.Provider value={{ ...toCallContext }}>
          {webphoneStates == states.CONNECTED ? <Call /> : <Calling />}
        </ToCallContext.Provider>
      </div>
    </>
  );
}
